import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { FaBars } from 'react-icons/fa6'
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react'
import useSession from '@/hooks/useSession'
import useOnboarding from '@/hooks/useOnboarding'

import logos from '@/assets/bookblast-logo-nav.png'

import LanguagePicker from '@components/LanguagePicker'
import { getContributionURL } from '@/util/helpers'

interface Page {
  url?: string
  name?: string
  key: string
  newtab?: boolean
}

interface HeaderProps {
  layout?: 'main' | 'auth'
}

export default function Header({ layout }: HeaderProps) {
  const { t } = useTranslation()
  const { session } = useSession()
  const { isOnboarding } = useOnboarding()
  const location = useLocation()
  const showMenu = !isOnboarding

  const allPages: Page[] = [
    { name: 'progress', key: 'content.components.navigation.progress_tracker' },
    { name: 'invite', key: 'content.components.navigation.invite_contacts' },
    {
      url: getContributionURL(session?.schoolSlug || '', session?.parentSlug || ''),
      key: 'content.components.navigation.contribute',
      newtab: true,
    },
    { name: 'account', key: 'content.components.navigation.my_account' },
    { name: 'signout', key: 'content.components.navigation.signout' },
  ]

  const pages = showMenu ? allPages : allPages.filter((p) => p.name === 'signout')

  if (layout === 'auth') {
    return (
      <>
        <div id='header' className={`w-full bg-white`}>
          <div className='w-full md:w-5/6 lg:w-3/4 mx-auto flex flex-row w-full justify-between font-serif px-6 md:px-0 py-4'>
            <Link to={showMenu ? `/${session?.schoolSlug}/progress` : '#'}>
              <img className='xs:h-[40px] h-[60px] object-scale-down' src={logos} alt='BookBlast by Books Are Fun' />
            </Link>

            <div className='flex flex-row'>
              <LanguagePicker />
            </div>
          </div>
        </div>
      </>
    )
  }
  return (
    <>
      <div id='header' className={`w-full bg-white w-full inline-flex mx-auto font-serif `}>
        <div className='flex flex-row mx-auto py-4 justify-between w-full px-3 md:px-8 lg:px-0 lg:w-2/3'>
          <Link to={showMenu ? `/${session?.schoolSlug}/progress` : '#'}>
            <img className='xs:h-[40px] h-[60px] object-scale-down' src={logos} alt='BookBlast by Books Are Fun' />
          </Link>
          <div className='flex flex-row gap-6 font-sans text-xs'>
            <div className='flex flex-row'>
              <LanguagePicker />
            </div>
            <Menu as='div' className='relative ml-3'>
              <MenuButton className='relative flex group hover:cursor-pointer focus-visible:none focus:outline-none'>
                <span className='absolute -inset-1.5' />
                <span className='sr-only'>Open navigation menu</span>
                <FaBars className='mt-2 w-10 h-10 text-leaf-green group-hover:text-cta ' />
              </MenuButton>

              <MenuItems
                transition
                className='absolute right-0 z-10 mt-2 w-60 origin-top-right rounded-md bg-white py-1 shadow-lg outline outline-1 outline-black/5 transition data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in'
              >
                {pages.map((page) => {
                  const href = page.name ? `./${page.name}` : page.url
                  return (
                    <MenuItem key={page.key}>
                      <Link
                        to={href ?? '/'}
                        {...(page.newtab === true ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                        className={`block px-4 py-2 text-base ${
                          page.name && location.pathname.includes(page?.name)
                            ? 'text-cta'
                            : 'text-gray-700 data-[focus]:bg-cta-active/20 data-[focus]:text-cta data-[focus]:outline-none'
                        }`}
                      >
                        {t(page.key)}
                      </Link>
                    </MenuItem>
                  )
                })}
              </MenuItems>
            </Menu>
          </div>
        </div>
      </div>
    </>
  )
}

